import { useState } from 'react';
import { RefreshCw, Sparkles } from 'lucide-react';
import { reloadToLatest, useAppUpdateStore } from '../lib/appUpdate';
import { versionLabel } from '../constants/appVersion';

/**
 * ⭐ What a deploy under an open tab looks like (owner, 2026-10-04) — see `lib/appUpdate.ts`.
 *
 * - `available`: a CARD, bottom-right. ⚠️ No close button on purpose: the old build is on
 *   borrowed time. It does not block the page, so a half-written entry can be finished
 *   first — and the next view change reloads quietly anyway.
 * - `required`: a piece of the app has already failed to load, so the old build cannot
 *   continue. FULL SCREEN, above everything, one action.
 * Both reload through `reloadToLatest`, which saves pending changes first.
 */
export function AppUpdatePrompt() {
  const status = useAppUpdateStore(s => s.status);
  const latest = useAppUpdateStore(s => s.latest);
  if (status === 'current') return null;
  return status === 'required'
    ? <UpdateRequiredScreen />
    : <UpdateAvailableCard version={latest?.version ?? ''} />;
}

function ReloadButton({ label, className }: { label: string; className: string }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => { setBusy(true); void reloadToLatest(); }}
      className={className}
    >
      <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
      {busy ? 'Saving and reloading…' : label}
    </button>
  );
}

export function UpdateAvailableCard({ version }: { version: string }) {
  const label = versionLabel(version);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="update-available"
      className="fixed bottom-4 right-4 z-[90] w-[min(360px,calc(100vw-2rem))] rounded-xl border border-border bg-card shadow-2xl p-4"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-foreground">A new version of Metanoia is ready{label ? ` (${label})` : ''}</p>
          <p className="text-xs text-muted-foreground">Refresh to get it — your changes are saved first.</p>
        </div>
      </div>
      <ReloadButton
        label="Refresh"
        className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-70"
      />
    </div>
  );
}

export function UpdateRequiredScreen() {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-required-title"
      data-testid="update-required"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
    >
      <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 text-center shadow-2xl space-y-4">
        <Sparkles className="w-8 h-8 text-primary mx-auto" />
        <h1 id="update-required-title" className="text-xl font-semibold text-foreground">Metanoia has been updated</h1>
        <p className="text-sm text-muted-foreground">
          A new version was released while this tab was open. Reload to continue — your changes are saved first.
        </p>
        <ReloadButton
          label="Reload now"
          className="mx-auto flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium disabled:opacity-70"
        />
      </div>
    </div>
  );
}
